const TOKEN_KEY = "token";
const TOKEN_EXPIRY_KEY = "tokenExpiresAtUtc";
const REFRESH_TOKEN_KEY = "refreshToken";
const REFRESH_TOKEN_EXPIRY_KEY = "refreshTokenExpiresAtUtc";
const USER_ID_KEY = "userId";
const USER_ROLES_KEY = "roles";

const isBrowser = typeof window !== "undefined";

const setCookie = (name: string, value: string, expiresAtUtc: string) => {
  if (!isBrowser) return;

  const expires = new Date(expiresAtUtc).toUTCString();
  const secureFlag = window.location.protocol === "https:" ? "; Secure" : "";

  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(
    value,
  )}; Expires=${expires}; Path=/; SameSite=Strict${secureFlag}`;
};

const getCookie = (name: string): string | null => {
  if (!isBrowser) return null;

  const cookies = document.cookie ? document.cookie.split("; ") : [];
  const target = `${encodeURIComponent(name)}=`;

  for (const c of cookies) {
    if (c.startsWith(target)) {
      return decodeURIComponent(c.substring(target.length));
    }
  }

  return null;
};

const deleteCookie = (name: string) => {
  if (!isBrowser) return;

  const secureFlag = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${encodeURIComponent(
    name,
  )}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/; SameSite=Strict${secureFlag}`;
};

export interface AuthData {
  userId: string;
  email: string;
  userName: string;
  roles: string[];
  token: string;
  tokenExpiresAtUtc: string;
  refreshToken: string;
  refreshTokenExpiresAtUtc: string;
}

export const getUserId = (): string | null => {
  if (!isBrowser) return null;
  return localStorage.getItem(USER_ID_KEY);
};

export const saveAuthData = (authData: AuthData): void => {
  setCookie(TOKEN_KEY, authData.token, authData.tokenExpiresAtUtc);
  setCookie(
    REFRESH_TOKEN_KEY,
    authData.refreshToken,
    authData.refreshTokenExpiresAtUtc,
  );
  // Store expiry times in cookies as well so frontend can read them
  setCookie(
    TOKEN_EXPIRY_KEY,
    authData.tokenExpiresAtUtc,
    authData.tokenExpiresAtUtc,
  );
  setCookie(
    REFRESH_TOKEN_EXPIRY_KEY,
    authData.refreshTokenExpiresAtUtc,
    authData.refreshTokenExpiresAtUtc,
  );
  localStorage.setItem(USER_ID_KEY, authData.userId);
  localStorage.setItem(USER_ROLES_KEY, JSON.stringify(authData.roles));
};

export const getToken = (): string | null => {
  return getCookie(TOKEN_KEY);
};

export const getTokenExpiryDate = (): Date | null => {
  const expiryDateStr = getCookie(TOKEN_EXPIRY_KEY);
  if (!expiryDateStr) return null;

  const expiryDate = new Date(expiryDateStr);
  if (Number.isNaN(expiryDate.getTime())) return null;

  return expiryDate;
};

export const isUserAuthenticated = (): boolean => {
  const token = getToken();
  if (!token) return false;

  const expiryDate = getTokenExpiryDate();
  if (!expiryDate) {
    return false;
  }

  // Check if token is expired (with 1 minute buffer to account for clock skew)
  const now = new Date();
  const bufferTime = 60 * 1000; // 1 minute in milliseconds
  return expiryDate.getTime() > now.getTime() + bufferTime;
};

export const clearToken = (): void => {
  deleteCookie(TOKEN_KEY);
  deleteCookie(REFRESH_TOKEN_KEY);
  deleteCookie(TOKEN_EXPIRY_KEY);
  deleteCookie(REFRESH_TOKEN_EXPIRY_KEY);
  localStorage.removeItem(USER_ID_KEY);
  localStorage.removeItem(USER_ROLES_KEY);
};

export const getMillisecondsUntilExpiry = (): number | null => {
  const expiryDate = getTokenExpiryDate();
  if (!expiryDate) {
    return null;
  }

  const now = new Date();
  const diff = expiryDate.getTime() - now.getTime();

  // Return null if already expired
  return diff > 0 ? diff : null;
};

export const getRefreshTokenExpiryDate = (): Date | null => {
  const expiryDateStr = getCookie(REFRESH_TOKEN_EXPIRY_KEY);
  if (!expiryDateStr) return null;

  const expiryDate = new Date(expiryDateStr);
  if (Number.isNaN(expiryDate.getTime())) return null;

  return expiryDate;
};

export const getMillisecondsUntilRefreshExpiry = (): number | null => {
  const expiryDate = getRefreshTokenExpiryDate();
  if (!expiryDate) return null;

  const now = new Date();
  const diff = expiryDate.getTime() - now.getTime();

  // Return null if already expired
  return diff > 0 ? diff : null;
};

export const getRefreshTokenFromCookies = (): string | null => {
  if (typeof document === "undefined") return null;

  const cookies = document.cookie ? document.cookie.split("; ") : [];
  const target = `${encodeURIComponent(REFRESH_TOKEN_KEY)}=`;

  for (const c of cookies) {
    if (c.startsWith(target)) {
      return decodeURIComponent(c.substring(target.length));
    }
  }

  return null;
};
